import { logger } from "../utils/logger";
import { secureStorage } from '../utils/secureStorage';
/**
 * Enterprise-Grade Audit Service for Security Event Logging
 *
 * Provides comprehensive audit logging for authentication events,
 * access control violations, and security-related activities.
 */
// import { logger } from "../utils/logger";
export interface AuditEvent {
    /** Unique event identifier */
    id: string;
    /** Event timestamp */
    timestamp: string;
    /** Event type/category */
    type: AuditEventType;
    /** Event severity level */
    severity: AuditSeverity;
    /** User information (if applicable) */
    user?: {
        id: string;
        email: string;
        role: string;
    };
    /** Event details */
    details: {
        action: string;
        resource?: string;
        outcome: 'success' | 'failure' | 'denied';
        reason?: string;
        metadata?: Record<string, any>;
    };
    /** Client information */
    client: {
        ip?: string;
        userAgent: string;
        sessionId?: string;
        correlationId?: string;
        timestamp: string;
        timezone: string;
    };
}
export enum AuditEventType {
    AUTHENTICATION = 'authentication',
    AUTHORIZATION = 'authorization',
    ACCESS_CONTROL = 'access_control',
    DATA_ACCESS = 'data_access',
    SECURITY_VIOLATION = 'security_violation',
    SYSTEM_EVENT = 'system_event'
}
export enum AuditSeverity {
    LOW = 'low',
    MEDIUM = 'medium',
    HIGH = 'high',
    CRITICAL = 'critical'
}
class AuditService {
    private static readonly STORAGE_KEY = 'audit_events';
    private static readonly MAX_EVENTS = 1000; // Keep last 1000 events in local storage
    private static readonly BATCH_SIZE = 50; // Send events in batches
    /**
     * Log an authentication event
     * @param event - Authentication event details
     */
    public static async logAuthentication(event: {
        user?: {
            id: string;
            email: string;
            role: string;
        };
        action: 'login' | 'logout' | 'login_failed' | 'token_refresh' | 'password_reset';
        outcome: 'success' | 'failure';
        reason?: string;
        metadata?: Record<string, any>;
    }): Promise<void> {
        await this.logEvent({
            type: AuditEventType.AUTHENTICATION,
            severity: event.outcome === 'failure' ? AuditSeverity.MEDIUM : AuditSeverity.LOW,
            user: event.user,
            details: {
                action: event.action,
                outcome: event.outcome,
                reason: event.reason,
                metadata: event.metadata
            }
        });
    }
    /**
     * Log an access control violation
     * @param event - Access control event details
     */
    public static logAccessControl(event: {
        user?: {
            id: string;
            email: string;
            role: string;
        };
        resource: string;
        requiredRoles?: string[];
        requiredPermissions?: string[];
        reason?: string;
        metadata?: Record<string, any>;
    }): void {
        this.logEvent({
            type: AuditEventType.ACCESS_CONTROL,
            severity: AuditSeverity.HIGH,
            user: event.user,
            details: {
                action: 'access_denied',
                resource: event.resource,
                outcome: 'denied',
                reason: event.reason,
                metadata: {
                    ...event.metadata,
                    requiredRoles: event.requiredRoles,
                    requiredPermissions: event.requiredPermissions
                }
            }
        });
    }
    /**
     * Log a denied access attempt (convenience method for ProtectedRoute)
     * @param user - User attempting access
     * @param resource - Resource being accessed
     * @param allowedRoles - Roles that would have been allowed
     */
    public static logDeniedAccess(user: any, resource: string, allowedRoles: string[]): void {
        this.logAccessControl({
            user: user ? {
                id: user.id?.toString() || 'unknown',
                email: user.email || 'unknown',
                role: user.role || 'unknown'
            } : undefined,
            resource,
            requiredRoles: allowedRoles,
            reason: 'Insufficient role permissions',
            metadata: {
                userAgent: navigator.userAgent,
                timestamp: new Date().toISOString(),
                path: resource
            }
        });
    }
    /**
     * Log access events from PolicyEngine
     * @param event - Access monitoring event from PolicyEngine
     */
    public static logAccessEvent(event: {
        type: 'access_granted' | 'access_denied' | 'policy_evaluation' | 'error';
        user: any;
        resource: string;
        result: any;
        correlationId: string;
        timestamp: string;
        metadata?: Record<string, any>;
    }): void {
        const severity = event.type === 'access_denied' || event.type === 'error'
            ? AuditSeverity.HIGH
            : AuditSeverity.LOW;
        this.logEvent({
            type: AuditEventType.ACCESS_CONTROL,
            severity,
            user: event.user ? {
                id: event.user.id?.toString() || 'unknown',
                email: event.user.email || 'unknown',
                role: event.user.role || 'unknown'
            } : undefined,
            details: {
                action: event.type,
                resource: event.resource,
                outcome: event.result.granted ? 'success' : 'denied',
                reason: event.result.reason,
                metadata: {
                    ...event.metadata,
                    correlationId: event.correlationId,
                    evaluatedPolicies: event.result.evaluatedPolicies,
                    failedConditions: event.result.failedConditions,
                    evaluationDuration: event.result.duration
                }
            }
        });
    }
    /**
     * Log a security violation
     * @param event - Security violation details
     */
    public static logSecurityViolation(event: {
        user?: {
            id: string;
            email: string;
            role: string;
        };
        violation: string;
        severity: AuditSeverity;
        details?: string;
        metadata?: Record<string, any>;
    }): void {
        this.logEvent({
            type: AuditEventType.SECURITY_VIOLATION,
            severity: event.severity,
            user: event.user,
            details: {
                action: event.violation,
                outcome: 'failure',
                reason: event.details,
                metadata: event.metadata
            }
        });
    }
    /**
     * Log a data access event
     * @param event - Data access event details
     */
    public static logDataAccess(event: {
        user?: {
            id: string;
            email: string;
            role: string;
        };
        action: 'read' | 'write' | 'delete' | 'export';
        resource: string;
        outcome: 'success' | 'failure';
        metadata?: Record<string, any>;
    }): void {
        this.logEvent({
            type: AuditEventType.DATA_ACCESS,
            severity: event.outcome === 'failure' ? AuditSeverity.MEDIUM : AuditSeverity.LOW,
            user: event.user,
            details: {
                action: event.action,
                resource: event.resource,
                outcome: event.outcome,
                metadata: event.metadata
            }
        });
    }
    /**
     * Log a system event
     * @param event - System event details
     */
    public static logSystemEvent(event: {
        action: string;
        severity: AuditSeverity;
        details?: string;
        metadata?: Record<string, any>;
    }): void {
        this.logEvent({
            type: AuditEventType.SYSTEM_EVENT,
            severity: event.severity,
            details: {
                action: event.action,
                outcome: 'success',
                reason: event.details,
                metadata: event.metadata
            }
        });
    }
    /**
     * Core logging method
     * @param event - Audit event to log
     */
    private static async logEvent(event: Omit<AuditEvent, 'id' | 'timestamp' | 'client'>): Promise<void> {
        try {
            const now = new Date();
            const auditEvent: AuditEvent = {
                id: this.generateEventId(),
                timestamp: now.toISOString(),
                client: {
                    userAgent: navigator.userAgent,
                    sessionId: this.getSessionId(),
                    correlationId: this.getCorrelationId(),
                    timestamp: now.toISOString(),
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    ip: this.getClientIP()
                },
                ...event
            };
            // Store event locally
            await this.storeEventLocally(auditEvent);
            // Send to remote logging service (if configured)
            this.sendToRemoteService(auditEvent);
            // Log to console in development
            if (import.meta.env.DEV) {
                logger.debug('[AuditService]', { "arg0": auditEvent });
            }
        }
        catch (error) {
            logger.error('[AuditService] Failed to log event:', error, {});
        }
    }
    /**
     * Store event in local storage for offline capability
     * @param event - Audit event to store
     */
    private static async storeEventLocally(event: AuditEvent): Promise<void> {
        try {
            const existingEvents = await this.getStoredEvents();
            existingEvents.push(event);
            // Keep only the most recent events
            if (existingEvents.length > this.MAX_EVENTS) {
                existingEvents.splice(0, existingEvents.length - this.MAX_EVENTS);
            }
            await secureStorage.setEncrypted(this.STORAGE_KEY, existingEvents);
        }
        catch (error) {
            logger.error('[AuditService] Failed to store event locally:', error, {});
        }
    }
    /**
     * Send event to remote logging service
     * @param event - Audit event to send
     */
    private static async sendToRemoteService(event: AuditEvent): Promise<void> {
        try {
            // In a real implementation, this would send to your logging service
            // (e.g., Sentry, Datadog, Splunk, etc.)
            // For now, we'll just simulate the API call
            if (import.meta.env.PROD) {
                // In production, send to actual logging service
                // await fetch('/api/audit/log', {
                //   method: 'POST',
                //   headers: { 'Content-Type': 'application/json' },
                //   body: JSON.stringify(event)
                // });
            }
        }
        catch (error) {
            logger.error('[AuditService] Failed to send event to remote service:', error, {});
        }
    }
    /**
     * Get stored events from local storage
     * @returns Array of stored audit events
     */
    private static async getStoredEvents(): Promise<AuditEvent[]> {
        try {
            const stored = await secureStorage.getEncrypted(this.STORAGE_KEY);
            return stored ? stored : [];
        }
        catch (error) {
            logger.error('[AuditService] Failed to retrieve stored events:', error, {});
            return [];
        }
    }
    /**
     * Get recent audit events
     * @param limit - Maximum number of events to return
     * @returns Array of recent audit events
     */
    public static getRecentEvents(limit: number = 100): AuditEvent[] {
        const events = this.getStoredEvents();
        return events.slice(-limit).reverse(); // Most recent first
    }
    /**
     * Get events by type
     * @param type - Event type to filter by
     * @param limit - Maximum number of events to return
     * @returns Array of filtered audit events
     */
    public static getEventsByType(type: AuditEventType, limit: number = 100): AuditEvent[] {
        const events = this.getStoredEvents();
        return events
            .filter(event => event.type === type)
            .slice(-limit)
            .reverse();
    }
    /**
     * Get events by severity
     * @param severity - Minimum severity level
     * @param limit - Maximum number of events to return
     * @returns Array of filtered audit events
     */
    public static getEventsBySeverity(severity: AuditSeverity, limit: number = 100): AuditEvent[] {
        const severityLevels = {
            [AuditSeverity.LOW]: 1,
            [AuditSeverity.MEDIUM]: 2,
            [AuditSeverity.HIGH]: 3,
            [AuditSeverity.CRITICAL]: 4
        };
        const events = this.getStoredEvents();
        return events
            .filter(event => severityLevels[event.severity] >= severityLevels[severity])
            .slice(-limit)
            .reverse();
    }
    /**
     * Clear old audit events
     * @param olderThan - Clear events older than this date
     */
    public static async clearOldEvents(olderThan: Date): Promise<void> {
        try {
            const events = await this.getStoredEvents();
            const filteredEvents = events.filter(event => new Date(event.timestamp) > olderThan);
            await secureStorage.setEncrypted(this.STORAGE_KEY, filteredEvents);
            logger.debug(`[AuditService] Cleared ${events.length - filteredEvents.length} old events`);
        }
        catch (error) {
            logger.error('[AuditService] Failed to clear old events:', error, {});
        }
    }
    /**
     * Generate unique event ID
     * @returns Unique event identifier
     */
    private static generateEventId(): string {
        return `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    /**
     * Get current session ID
     * @returns Session identifier
     */
    private static getSessionId(): string {
        try {
            let sessionId = sessionStorage.getItem('session_id');
            if (!sessionId) {
                sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                sessionStorage.setItem('session_id', sessionId);
            }
            return sessionId;
        }
        catch (error) {
            return `session_${Date.now()}`;
        }
    }
    /**
     * Get correlation ID for request tracing
     * @returns Correlation identifier
     */
    private static getCorrelationId(): string {
        try {
            let correlationId = sessionStorage.getItem('correlation_id');
            if (!correlationId) {
                correlationId = `corr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                sessionStorage.setItem('correlation_id', correlationId);
            }
            return correlationId;
        }
        catch (error) {
            return `corr_${Date.now()}`;
        }
    }
    /**
     * Get client IP address (if available)
     * @returns Client IP or undefined
     */
    private static getClientIP(): string | undefined {
        try {
            // In a real implementation, this would come from your backend
            // For now, we'll try to get it from various sources
            // Check if IP is stored in session (set by backend)
            const storedIP = sessionStorage.getItem('client_ip');
            if (storedIP) {
                return storedIP;
            }
            // In development, return a placeholder
            if (import.meta.env.DEV) {
                return '127.0.0.1';
            }
            return undefined;
        }
        catch (error) {
            return undefined;
        }
    }
}
export default AuditService;
